import { useState } from 'react';
import { api } from '../lib/api.js';

// Merge two duplicate user rows. CJ 2026-06-30: someone got entered
// twice (manual + Square import is the usual culprit). Owner picks a
// keeper, sees a dry-run preview of what'll be rewritten, and
// confirms — the server reroutes every user_id-bearing FK in one
// transaction and tombstones the loser the same way hard-delete does.
export function MergeUsersModal({ currentUser, loser, allUsers, onClose, onMerged }) {
  const [keeperId, setKeeperId] = useState(null);
  const [preview, setPreview] = useState(null);   // { columns, columns_list } from dry run
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [phase, setPhase] = useState('pick');      // 'pick' | 'confirm' | 'done'
  const [result, setResult] = useState(null);
  const headers = { 'x-user-id': String(currentUser.id) };
  const candidates = (allUsers || [])
    .filter((u) => u.id !== loser.id && u.id !== currentUser.id && (u.status || 'active') !== 'deleted')
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  async function runDryRun(id) {
    setBusy(true); setErr('');
    try {
      const r = await api('POST', `/api/admin/users/${loser.id}/merge-into?dry=1`,
        { keeper_id: id }, headers);
      setPreview(r);
      setPhase('confirm');
    } catch (e) {
      let m = e?.message || 'Preview failed';
      try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
      setErr(m);
    } finally { setBusy(false); }
  }
  async function runMerge() {
    setBusy(true); setErr('');
    try {
      const r = await api('POST', `/api/admin/users/${loser.id}/merge-into`,
        { keeper_id: keeperId }, headers);
      setResult(r); setPhase('done');
    } catch (e) {
      let m = e?.message || 'Merge failed';
      try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
      setErr(m);
    } finally { setBusy(false); }
  }

  const keeper = candidates.find((c) => c.id === keeperId) || null;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card"
        style={{ width: '100%', maxWidth: 520, padding: 18, maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Merge duplicate users</div>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }}>Close</button>
        </div>
        <div style={{ fontSize: 13, color: '#37474f', marginBottom: 12 }}>
          Merge <b>{loser.name}</b>{loser.email ? ` (${loser.email})` : ''} into another user.
          All of their shifts, punches, completions, and chat history get
          re-attributed to the keeper, and this row is tombstoned.
          <br />
          <span style={{ color: '#a16207', fontSize: 12 }}>
            This is one-way — pick the wrong keeper and you'll need a DB restore to undo.
          </span>
        </div>

        {phase === 'pick' && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#757575', marginBottom: 6 }}>
              Keep which user? (this is the one that survives)
            </div>
            <select value={keeperId || ''} onChange={(e) => setKeeperId(Number(e.target.value) || null)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }}>
              <option value="">— choose the keeper —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.email ? ` · ${c.email}` : ''}{c.status === 'archived' ? ' · archived' : ''}
                </option>
              ))}
            </select>
            {err && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 8 }}>{err}</div>}
            <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Cancel</button>
              <button onClick={() => keeperId && runDryRun(keeperId)} disabled={!keeperId || busy}
                style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: !keeperId || busy ? 0.5 : 1 }}>
                {busy ? 'Checking…' : 'Preview merge'}
              </button>
            </div>
          </>
        )}

        {phase === 'confirm' && preview && keeper && (
          <>
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, padding: 10, fontSize: 13, color: '#92400e', marginBottom: 12 }}>
              <div><b>{loser.name}</b> → <b>{keeper.name}</b></div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {preview.columns} user-id column{preview.columns === 1 ? '' : 's'} across the schema will be rewritten.
                {loser.email && (<div>The email <code>{loser.email}</code> will be freed up.</div>)}
              </div>
            </div>
            {(preview.columns_list || []).length > 0 && (
              <details style={{ marginBottom: 10 }}>
                <summary style={{ fontSize: 12, color: '#757575', cursor: 'pointer' }}>
                  Show columns ({preview.columns_list.length})
                </summary>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#37474f', maxHeight: 180, overflow: 'auto',
                              border: '1px solid #f0f0f0', borderRadius: 4, padding: 8, marginTop: 6 }}>
                  {preview.columns_list.map((c) => (
                    <div key={`${c.table}.${c.col}`}>{c.table}.{c.col}</div>
                  ))}
                </div>
              </details>
            )}
            {err && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setPhase('pick'); setPreview(null); }}
                className="btn-secondary" style={{ fontSize: 12 }}>Back</button>
              <button onClick={runMerge} disabled={busy}
                style={{ background: '#5b21b6', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>
                {busy ? 'Merging…' : `Merge ${loser.name} → ${keeper.name}`}
              </button>
            </div>
          </>
        )}

        {phase === 'done' && result && (
          <>
            <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, padding: 10, fontSize: 13, color: '#166534', marginBottom: 12 }}>
              Merged. {result.total_rows_rewritten} row{result.total_rows_rewritten === 1 ? '' : 's'} re-attributed across {result.rewrote?.length || 0} column{(result.rewrote?.length || 0) === 1 ? '' : 's'}.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onMerged}
                style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
